import React, { useEffect, useState, useCallback } from "react";
import { Box, Input } from "@mui/joy";
import { useNavigate } from "react-router-dom";
import EmptyData from "../../components/EmptyData";
import CustomPagination from "../../components/CustomPagination";
import { getAutoBidListAPI } from "../../reduxData/user/userAction";
import { toast } from "react-toastify";

const AutobidDataOverview = () => {
  const navigate = useNavigate();

  const [autoBids, setAutoBids] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalRecords, setTotalRecords] = useState(0);
  
  const [userSearch, setUserSearch] = useState("");
  const [itemSearch, setItemSearch] = useState("");

  const [pagination, setPagination] = useState({ page: 1, pageSize: 10 });

  const DefaultImg =
    "https://res.cloudinary.com/dqemehfm3/image/upload/v1754943113/item/image/thumb-1754943113396-pixel_search.png";

  const fetchAutoBids = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getAutoBidListAPI(
        pagination.page,
        pagination.pageSize,
        userSearch,
        itemSearch
      );
      setAutoBids(response?.data || []);
      setTotalRecords(response?.total || 0);
    } catch (error) {
      toast.error("Failed to load auto-bids.");
    } finally {
      setIsLoading(false);
    }
  }, [pagination, userSearch, itemSearch]);

  useEffect(() => {
    const handler = setTimeout(() => {
      fetchAutoBids();
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [fetchAutoBids, userSearch, itemSearch]);

  const handlePageChange = (newPage, newPageSize) => {
    setPagination({ page: newPage, pageSize: newPageSize });
  };

  const handleUserSearchChange = (e) => {
    setUserSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleItemSearchChange = (e) => {
    setItemSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  return (
    <div>
      <div className="container">
        <Box className="d-flex flex-column flex-md-row mb-4 justify-content-between align-items-center gap-3">
          <h3 className="mb-0 fw-600">{`Active Auto Bids (${totalRecords})`}</h3>
          
          <div className="d-flex gap-2">
            <Input 
              placeholder="Search User..." 
              value={userSearch}
              onChange={handleUserSearchChange}
              sx={{ width: 250 }}
            />
            <Input 
              placeholder="Search Item..." 
              value={itemSearch}
              onChange={handleItemSearchChange}
              sx={{ width: 250 }}
            />
          </div>
        </Box>

        <div className="table-responsive">
          <table className="table user-management-table">
            <thead>
              <tr>
                <th>User</th>
                <th>Item</th>
                <th>Max Amount</th>
                <th>Created At</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="4" className="text-center">
                    Loading...
                  </td>
                </tr>
              ) : autoBids.length > 0 ? (
                autoBids.map((bid) => (
                  <tr key={bid._id}>
                    <td
                      style={{ textAlign: "left", maxWidth: "200px" }}
                      className="clickable-cell"
                    >
                      <div
                        className="d-flex align-items-center justify-content-left cursor-pointer"
                        onClick={() =>
                          navigate(`/editUser/${bid?.userId?._id}`)
                        }
                      >
                        <span>
                          <img
                            src={
                              bid?.userId?.profile_pic
                                ? `${bid?.userId?.profile_pic}`
                                : DefaultImg
                            }
                            style={{ width: 40, height: 40, borderRadius: 75 }}
                            className="object-fit-cover "
                            alt=""
                          />
                        </span>
                        <div className="px-2">
                          <span className="text-capitalize fw-600">
                            {bid?.userId?.user_name || "Unknown"}
                          </span>
                          <br />
                          <span className="text-muted small">
                            {bid?.userId?.email}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td
                      style={{
                        textAlign: "left",
                        maxWidth: "250px",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      className="clickable-cell"
                    >
                      <a
                        href={`https://insertbid.com/auction/${bid?.itemId?.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ width: "100%", height: "100%", textDecoration: "none", color: "inherit" }}
                      >
                         <span className="fw-600">{bid?.itemId?.item_name || "N/A"}</span>
                         {bid?.itemId?.item_sold && (
                             <span className="badge bg-danger ms-2" style={{fontSize: '0.7rem'}}>Sold</span>
                         )}
                      </a>
                    </td>

                    <td>
                      <span className="fw-bold text-success">
                        ${bid?.maxAmount?.toLocaleString()}
                      </span>
                    </td>

                    <td>
                      {bid?.createdAt
                        ? new Date(bid.createdAt).toLocaleDateString() + " " + new Date(bid.createdAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})
                        : "N/A"}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="4">
                    <EmptyData />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="mt-3">
          {totalRecords > pagination.pageSize && (
            <CustomPagination
              total={totalRecords}
              onPageChange={handlePageChange}
              currentPage={pagination.page}
              pageSize={pagination.pageSize}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AutobidDataOverview;